import { supabase } from '@/integrations/supabase/client';

// Note: Email preference checking and rate limiting is now handled
// server-side in the Supabase Edge Function at /functions/send-email

export interface EmailRequest {
  emailType: string;
  recipientEmail: string;
  templateData: Record<string, unknown>;
  scheduledAt?: string;
  priority?: 'high' | 'medium' | 'low';
}

export interface EmailResponse {
  success: boolean;
  emailId?: string;
  message?: string;
  error?: string;
}

class EmailAPI {
  private readonly functionUrl: string;

  constructor() {
    // Construct the function URL
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    this.functionUrl = `${supabaseUrl}/functions/v1/send-email`;
  }

  async sendEmail(request: EmailRequest): Promise<EmailResponse> {
    try {
      // Get current session
      const {
        data: { session },
        error: sessionError,
      } = await supabase.auth.getSession();

      if (sessionError || !session) {
        throw new Error('User not authenticated');
      }

      const response = await fetch(this.functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify(request),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to send email');
      }

      return data;
    } catch (error) {
      console.error('Email API error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  // Convenience methods for common email types
  async sendWelcomeEmail(
    userEmail: string,
    userData: {
      firstName: string;
      userType: string;
    }
  ): Promise<EmailResponse> {
    return this.sendEmail({
      emailType: 'welcome',
      recipientEmail: userEmail,
      templateData: userData,
      priority: 'high',
    });
  }

  async sendProfileReminder(
    userEmail: string,
    userData: {
      firstName: string;
      userType: string;
      missingItems: string[];
    }
  ): Promise<EmailResponse> {
    return this.sendEmail({
      emailType: 'profile_reminder',
      recipientEmail: userEmail,
      templateData: userData,
      priority: 'medium',
    });
  }

  async sendJobMatchNotification(
    filmmakerEmail: string,
    jobData: {
      firstName: string;
      jobTitle: string;
      location: string;
      startDate: string;
      budgetRange: string;
      description: string;
      clientName: string;
      clientRating?: number;
      role?: string;
      jobId: string;
    }
  ): Promise<EmailResponse> {
    return this.sendEmail({
      emailType: 'job_match',
      recipientEmail: filmmakerEmail,
      templateData: jobData,
      priority: 'medium',
    });
  }

  async sendApplicationSubmittedConfirmation(
    filmmakerEmail: string,
    applicationData: {
      jobTitle: string;
      clientName: string;
      bidAmount: number;
      timestamp: string;
      // Navigation IDs for clickable notifications
      job_id?: string;
      bid_id?: string;
    }
  ): Promise<EmailResponse> {
    return this.sendEmail({
      emailType: 'application_submitted',
      recipientEmail: filmmakerEmail,
      templateData: applicationData,
      priority: 'high',
    });
  }

  // Application received notification (to client)
  async sendApplicationReceivedNotification(
    clientEmail: string,
    applicationData: {
      clientName: string;
      filmmakerName: string;
      jobTitle: string;
      bidAmount: number;
      coverLetterExcerpt: string;
      timestamp: string;
      applicationUrl: string;
      // Navigation IDs for clickable notifications
      job_id?: string;
      bid_id?: string;
    }
  ): Promise<EmailResponse> {
    return this.sendEmail({
      emailType: 'application_received',
      recipientEmail: clientEmail,
      templateData: applicationData,
      priority: 'high',
    });
  }

  async sendBookingConfirmation(
    recipientEmail: string,
    bookingData: {
      jobTitle: string;
      clientName: string;
      filmmakerName: string;
      projectDates: string;
      location: string;
      agreedAmount: number;
      role: string;
      isForFilmmaker: boolean;
      isContractOffer?: boolean; // When client signs first - offer to filmmaker
      isBookingConfirmed?: boolean; // When filmmaker signs - final confirmation
    }
  ): Promise<EmailResponse> {
    return this.sendEmail({
      emailType: 'booking_confirmation',
      recipientEmail: recipientEmail,
      templateData: bookingData,
      priority: 'high',
    });
  }

  async sendMessageNotification(
    recipientEmail: string,
    messageData: {
      senderName: string;
      messagePreview: string;
      context?: string;
      recipientRole?: 'client' | 'filmmaker';
      // Navigation IDs for clickable notifications
      room_name?: string;
      sender_id?: string;
      sender_role?: string;
    }
  ): Promise<EmailResponse> {
    // Note: Activity checking is complex for cross-user messaging since we'd need
    // to check the recipient's activity, not the sender's. Rate limiting and
    // email preferences provide sufficient spam protection.
    return this.sendEmail({
      emailType: 'message_notification',
      recipientEmail: recipientEmail,
      templateData: messageData,
      priority: 'medium',
    });
  }

  async sendReviewRequest(
    userEmail: string,
    reviewData: {
      userName: string;
      counterpartyName: string;
      jobTitle: string;
    }
  ): Promise<EmailResponse> {
    return this.sendEmail({
      emailType: 'review_request',
      recipientEmail: userEmail,
      templateData: reviewData,
      priority: 'low',
    });
  }

  async sendWeeklyDigest(
    userEmail: string,
    digestData: {
      firstName: string;
      jobMatches?: Record<string, unknown>[];
      profileStats?: Record<string, unknown>;
    }
  ): Promise<EmailResponse> {
    return this.sendEmail({
      emailType: 'weekly_digest',
      recipientEmail: userEmail,
      templateData: digestData,
      priority: 'low',
    });
  }

  // Client payment reminder
  async sendPaymentReminderToClient(
    clientEmail: string,
    reminderData: {
      clientName: string;
      filmmakerName: string;
      jobTitle: string;
      jobAmount: number;
      daysOverdue: number;
      jobId: string;
    }
  ): Promise<EmailResponse> {
    const priority = reminderData.daysOverdue >= 5 ? 'high' : 'medium';

    return this.sendEmail({
      emailType: 'payment_reminder',
      recipientEmail: clientEmail,
      templateData: reminderData,
      priority,
    });
  }

  // Filmmaker payout notification
  async sendPayoutNotificationToFilmmaker(
    filmmakerEmail: string,
    payoutData: {
      filmmakerName: string;
      clientName: string;
      jobTitle: string;
      grossAmount: number;
      platformFee: number;
      netAmount: number;
      payoutMethod: string;
      transactionId: string;
      estimatedArrival: string;
    }
  ): Promise<EmailResponse> {
    return this.sendEmail({
      emailType: 'payout_notification',
      recipientEmail: filmmakerEmail,
      templateData: payoutData,
      priority: 'high',
    });
  }

  // Deliverable submission notification (to client)
  async sendDeliverableSubmitted(
    clientEmail: string,
    deliverableData: {
      clientName: string;
      filmmakerName: string;
      jobTitle: string;
      deliverableTitle: string;
      submissionMessage?: string;
      reviewDeadline?: string;
    }
  ): Promise<EmailResponse> {
    return this.sendEmail({
      emailType: 'deliverable_submitted',
      recipientEmail: clientEmail,
      templateData: deliverableData,
      priority: 'high',
    });
  }

  // Deliverable approved notification (to filmmaker)
  async sendDeliverableApproved(
    filmmakerEmail: string,
    deliverableData: {
      filmmakerName: string;
      clientName: string;
      jobTitle: string;
      deliverableTitle: string;
      approvalMessage?: string;
      nextSteps?: string;
    }
  ): Promise<EmailResponse> {
    return this.sendEmail({
      emailType: 'deliverable_approved',
      recipientEmail: filmmakerEmail,
      templateData: deliverableData,
      priority: 'medium',
    });
  }

  // Deliverable rejected/revision requested notification (to filmmaker)
  async sendDeliverableRevisionRequested(
    filmmakerEmail: string,
    deliverableData: {
      filmmakerName: string;
      clientName: string;
      jobTitle: string;
      deliverableTitle: string;
      revisionNotes: string;
      revisionDeadline?: string;
    }
  ): Promise<EmailResponse> {
    return this.sendEmail({
      emailType: 'deliverable_revision_requested',
      recipientEmail: filmmakerEmail,
      templateData: deliverableData,
      priority: 'high',
    });
  }

  // Verification reminder notification
  async sendVerificationReminder(
    userEmail: string,
    reminderData: {
      firstName: string;
      userType: string;
      daysSinceSignup: number;
      verificationUrl: string;
    }
  ): Promise<EmailResponse> {
    return this.sendEmail({
      emailType: 'verification_reminder',
      recipientEmail: userEmail,
      templateData: reminderData,
      priority: 'high',
    });
  }

  // Account approved/verified notification
  async sendAccountApproved(
    userEmail: string,
    approvalData: {
      firstName: string;
      userType: string;
      approvedBy: string;
      approvalDate: string;
      nextSteps?: string[];
    }
  ): Promise<EmailResponse> {
    return this.sendEmail({
      emailType: 'account_approved',
      recipientEmail: userEmail,
      templateData: approvalData,
      priority: 'high',
    });
  }

  // Review reminder notification
  async sendReviewReminder(
    userEmail: string,
    reviewData: {
      userName: string;
      counterpartyName: string;
      counterpartyRole: string;
      jobTitle: string;
      completionDate: string;
      reviewUrl: string;
      daysSinceCompletion: number;
    }
  ): Promise<EmailResponse> {
    return this.sendEmail({
      emailType: 'review_reminder',
      recipientEmail: userEmail,
      templateData: reviewData,
      priority: 'medium',
    });
  }

  // Batch email sending
  async sendBulkEmails(requests: EmailRequest[]): Promise<EmailResponse[]> {
    const promises = requests.map((request) => this.sendEmail(request));
    return Promise.all(promises);
  }

  // Schedule email for later
  async scheduleEmail(
    request: EmailRequest,
    scheduledDate: Date
  ): Promise<EmailResponse> {
    return this.sendEmail({
      ...request,
      scheduledAt: scheduledDate.toISOString(),
    });
  }
}

// Export singleton instance
export const emailAPI = new EmailAPI();
